import { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import { useUserAchievements } from '@/hooks/useAchievements';
import { AchievementBadge } from './AchievementBadge';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

export function AchievementNotificationBell() {
  const { user } = useAuth();
  const { data: userAchievements } = useUserAchievements(user?.id);
  const [unseenCount, setUnseenCount] = useState(0);
  const [open, setOpen] = useState(false);

  // Calculate unseen achievements (unlocked within last 24 hours and not marked as seen)
  useEffect(() => {
    if (!userAchievements) return;

    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);

    const unseen = userAchievements.filter(ua => {
      const unlockedAt = new Date(ua.unlocked_at);
      return unlockedAt > oneDayAgo;
    });

    setUnseenCount(unseen.length);
  }, [userAchievements]);

  const handleOpen = async (isOpen: boolean) => {
    setOpen(isOpen);
    
    // When opening, mark all as "seen" by clearing the unseen count
    if (isOpen && unseenCount > 0) {
      // Just clear the local state - we're using a simple time-based approach
      // for "unseen" rather than tracking seen_at in the database
      setTimeout(() => setUnseenCount(0), 500);
    }
  };

  const recentAchievements = userAchievements?.slice(0, 5) || [];

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative hidden md:flex"
        >
          <Trophy className="h-5 w-5" />
          {unseenCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 min-w-5 text-xs px-1 flex items-center justify-center"
            >
              {unseenCount >= 9 ? '9+' : unseenCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Recent Achievements</h3>
            <Link 
              to="/achievements" 
              className="text-sm text-primary hover:underline"
              onClick={() => setOpen(false)}
            >
              View All
            </Link>
          </div>
        </div>
        
        <ScrollArea className="h-[300px]">
          {recentAchievements.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <p>No achievements unlocked yet.</p>
              <p className="text-sm mt-1">Keep reading to earn your first achievement!</p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {recentAchievements.map((userAchievement) => {
                const achievement = userAchievement.achievement;
                if (!achievement) return null;

                return (
                  <Link
                    key={userAchievement.id}
                    to="/achievements"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <AchievementBadge 
                      achievement={achievement} 
                      unlocked={true}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{achievement.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(userAchievement.unlocked_at), { addSuffix: true })}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      +{achievement.xp_reward} XP
                    </Badge>
                  </Link>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
